import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Checkbox, TextButton, Typography, DropdownMenu, useOverlayContext, ConfirmationModal, RadioButtonWithLabel } from 'seagull';
import { ChevronDown } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { getVendorReminderDays, getClientReminderDays, getClientReminderTimes } from '~/business-logic/notificationSettings';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { NotificationStatus, NotificationSettingsEntryType, NotificationSettingsModel } from '~/models/NotificationSettingsModel';
import { selectors } from '~/store/state-selectors';
import styles from './EditNotificationSettingsModal.module.scss';
import { Tab, Table, isDays, minutesToDays, daysToMinutes, deriveLegacyIsActive, handleSetRecipientTypes, getJoinedRecipientTypesText, getFallbackRecipientTypes, getEditDropdownRecipientTypeText, notificationSettingsTextsPath } from './NotificationSettings.utils';
export const GetEditNotificationSettingsModalTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            EditNotificationSettingsModalTID: {
                Modal: 'edit-notification-modal',
                NotificationText: 'edit-notification-modal-notification-text',
                Save: 'edit-notification-modal-save',
                Cancel: 'edit-notification-modal-cancel',
                DoNotSend: 'do-not-send-notification',
                Notify: 'notify-recipients',
                RecipientTypes: 'edit-recipient-types',
                RecipientItem: (type)=>"edit-recipient-types-dropdown-item-".concat(type),
                Days: 'edit-days',
                DaysItem: (daysOption)=>"edit-days-dropdown-item-".concat(daysOption),
                Times: 'edit-times',
                TimesItem: (timesOption)=>"edit-times-dropdown-item-".concat(timesOption)
            }
        }
    });
export const EditNotificationSettingsModal = (param)=>{
    let { tableType, selectedTab, notification, notificationText, notificationName, refresh, dismissModal } = param;
    const { EditNotificationSettingsModalTID } = useMemo(()=>GetEditNotificationSettingsModalTID(), []);
    const { t: notificationSettingsTexts } = useLocaleKeys(notificationSettingsTextsPath);
    const { t: commonTexts } = useLocaleKeys('common');
    const { api } = useStoreActions();
    const navigate = useNavigate();
    const businessId = useStore(selectors.user.businessId);
    const overlayContext = useOverlayContext();
    const notificationSettings = new NotificationSettingsModel(notification);
    const [times, setTimes] = useState(notificationSettings.recurringTimes());
    const [minutes, setMinutes] = useState(notificationSettings.recurringMinutes());
    const [recipientTypes, setRecipientTypes] = useState(deriveLegacyIsActive(notification) ? notification.recipientTypes : []);
    const handleToggle = ()=>{
        setRecipientTypes((recipientTypes)=>getFallbackRecipientTypes({
                recipientTypes,
                defaultRecipientType: notification.recipientTypeOptions[0],
                originalRecipientTypes: notification.recipientTypes
            }));
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'edit_notification_settings_confirm'
            },
            secondaryButton: {
                name: 'edit_notification_settings_cancel'
            }
        },
        title: notificationSettingsTexts.editTitle(),
        testId: EditNotificationSettingsModalTID.Modal(),
        content: /*#__PURE__*/ _jsxs(Box, {
            className: styles.body,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    weight: "bold",
                    testId: EditNotificationSettingsModalTID.NotificationText(),
                    children: notificationText
                }),
                /*#__PURE__*/ _jsx(EditModalOptions, {
                    times: times,
                    minutes: minutes,
                    editKey: "".concat(selectedTab, "-").concat(tableType),
                    recipientTypes: recipientTypes,
                    notificationName: notificationName,
                    recipientTypeOptions: notification.recipientTypeOptions,
                    setMinutes: setMinutes,
                    setTimes: setTimes,
                    setRecipientTypes: setRecipientTypes,
                    handleToggle: handleToggle
                })
            ]
        }),
        primaryButtonLabel: commonTexts.buttons.save(),
        primaryButtonTestId: EditNotificationSettingsModalTID.Save(),
        onPrimaryButtonClick: async ()=>{
            if (!businessId) {
                navigate(routes.home);
                return;
            }
            overlayContext.startLoader('UpdateNotificationSettings');
            const saveRequest = NotificationSettingsModel.hasRecipients({
                recipientTypes
            }) ? api.notification.updateNotificationSettings({
                data: notification.type === NotificationSettingsEntryType.Recurring ? notificationSettings.makeRecurringNotificationSettingsEntryUpdate(recipientTypes, {
                    minutes: minutes || notification.recurring.minutes,
                    times: times || notification.recurring.times
                }) : notificationSettings.makeSingleNotificationSettingsEntryUpdate(recipientTypes),
                businessId,
                notificationName
            }) : api.notification.setNotificationStatusV2({
                businessId,
                notificationName,
                status: NotificationStatus.Silent
            });
            const result = await handlePromise(saveRequest);
            if (result.success) {
                await refresh();
                overlayContext.showSuccessToast(commonTexts.toasts.notificationSettingsUpdatedSuccessfully(), 'edit-notification-success');
            } else {
                overlayContext.showErrorToast(commonTexts.toasts.failedToUpdateNotificationSettings(), 'edit-notification-error');
            }
            overlayContext.stopLoader('UpdateNotificationSettings');
            dismissModal();
        },
        secondaryButtonLabel: commonTexts.buttons.cancel(),
        secondaryButtonTestId: EditNotificationSettingsModalTID.Cancel(),
        onSecondaryButtonClick: dismissModal,
        onCloseButtonClick: dismissModal
    });
};
const EditModalOptions = (param)=>{
    let { times, minutes, editKey, recipientTypes, notificationName, recipientTypeOptions, setTimes, setMinutes, setRecipientTypes, handleToggle } = param;
    const { EditNotificationSettingsModalTID } = useMemo(()=>GetEditNotificationSettingsModalTID(), []);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'do_not_send_notification'
                },
                testId: EditNotificationSettingsModalTID.DoNotSend(),
                label: /*#__PURE__*/ _jsx(Typography, {
                    className: styles.option,
                    children: editDetailsMap[editKey].doNotSendText
                }),
                checked: !NotificationSettingsModel.hasRecipients({
                    recipientTypes
                }),
                onChange: handleToggle
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'notify_recipients'
                },
                testId: EditNotificationSettingsModalTID.Notify(),
                label: /*#__PURE__*/ _jsxs(Typography, {
                    className: styles.option,
                    children: [
                        "".concat(editDetailsMap[editKey].sendText, " "),
                        editDetailsMap[editKey].render({
                            times,
                            minutes,
                            recipientTypes,
                            recipientTypeOptions,
                            notificationName,
                            setTimes,
                            setMinutes,
                            setRecipientTypes
                        })
                    ]
                }),
                checked: NotificationSettingsModel.hasRecipients({
                    recipientTypes
                }),
                onChange: handleToggle
            })
        ]
    });
};
const DropdownButton = (param)=>{
    let { testId, dropdownItems, children } = param;
    return children && /*#__PURE__*/ _jsx(DropdownMenu, {
        hAlign: "left",
        maxHeight: "50%",
        target: /*#__PURE__*/ _jsx(TextButton, {
            bi: {
                name: 'open_notification_setting_details'
            },
            weight: "bold",
            suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {}),
            testId: testId,
            className: styles.textButton,
            children: children
        }),
        children: dropdownItems
    });
};
const editDetailsMap = {
    ["".concat(Tab.Vendor, "-").concat(Table.Notifications)]: {
        sendText: 'Notify',
        doNotSendText: 'Do not notify',
        render: (param)=>{
            let { recipientTypes, notificationName, recipientTypeOptions, setRecipientTypes } = param;
            return NotificationSettingsModel.hasRecipients({
                recipientTypes
            }) && /*#__PURE__*/ _jsx(RecipientTypesDropdown, {
                recipientTypes: recipientTypes,
                notificationName: notificationName,
                recipientTypeOptions: recipientTypeOptions,
                setRecipientTypes: setRecipientTypes
            });
        }
    },
    ["".concat(Tab.Vendor, "-").concat(Table.Reminders)]: {
        sendText: 'Notify',
        doNotSendText: 'Do not notify',
        render: (param)=>{
            let { minutes, recipientTypes, notificationName, recipientTypeOptions, setMinutes, setRecipientTypes } = param;
            return !!minutes && NotificationSettingsModel.hasRecipients({
                recipientTypes
            }) && /*#__PURE__*/ _jsxs(Box, {
                as: "span",
                children: [
                    /*#__PURE__*/ _jsx(RecipientTypesDropdown, {
                        recipientTypes: recipientTypes,
                        notificationName: notificationName,
                        recipientTypeOptions: recipientTypeOptions,
                        setRecipientTypes: setRecipientTypes
                    }),
                    "after ",
                    /*#__PURE__*/ _jsx(DaysDropdown, {
                        options: getVendorReminderDays(),
                        minutes: minutes,
                        setMinutes: setMinutes
                    })
                ]
            });
        }
    },
    ["".concat(Tab.Client, "-").concat(Table.Notifications)]: {
        sendText: 'Notify my client',
        doNotSendText: 'Do not notify my client',
        render: ()=>/*#__PURE__*/ _jsx(_Fragment, {})
    },
    ["".concat(Tab.Client, "-").concat(Table.Reminders)]: {
        sendText: 'Remind my client ',
        doNotSendText: 'Do not remind my client',
        render: (param)=>{
            let { times, minutes, recipientTypes, setTimes, setMinutes } = param;
            return !!minutes && !!times && NotificationSettingsModel.hasRecipients({
                recipientTypes
            }) && /*#__PURE__*/ _jsxs(Box, {
                as: "span",
                children: [
                    "every ",
                    /*#__PURE__*/ _jsx(DaysDropdown, {
                        options: getClientReminderDays(),
                        minutes: minutes,
                        setMinutes: setMinutes
                    }),
                    "up to ",
                    /*#__PURE__*/ _jsx(TimesDropdown, {
                        times: times,
                        setTimes: setTimes
                    })
                ]
            });
        }
    }
};
const RecipientTypesDropdown = (param)=>{
    let { recipientTypes, notificationName, recipientTypeOptions, setRecipientTypes } = param;
    const { EditNotificationSettingsModalTID } = useMemo(()=>GetEditNotificationSettingsModalTID(), []);
    const { t: notificationSettingsTexts } = useLocaleKeys(notificationSettingsTextsPath);
    const featureFlags = useFeatureFlags();
    return /*#__PURE__*/ _jsx(DropdownButton, {
        dropdownItems: recipientTypeOptions.map((type)=>/*#__PURE__*/ _jsx(DropdownMenu.Item, {
                bi: {
                    name: 'edit_recipient_types_dropdown_item'
                },
                as: Checkbox,
                closeOnClick: false,
                checked: recipientTypes.includes(type),
                onChange: (e)=>handleSetRecipientTypes({
                        newType: type,
                        newRecipientTypes: e.currentTarget.checked ? [
                            ...recipientTypes,
                            type
                        ] : recipientTypes.filter((x)=>x !== type),
                        setRecipientTypes
                    }),
                testId: EditNotificationSettingsModalTID.RecipientItem(type),
                children: /*#__PURE__*/ _jsx(Typography, {
                    children: getEditDropdownRecipientTypeText({
                        notificationName,
                        texts: notificationSettingsTexts
                    })[type]
                })
            }, type)),
        testId: EditNotificationSettingsModalTID.RecipientTypes(),
        children: getJoinedRecipientTypesText({
            recipientTypes,
            notificationName,
            texts: notificationSettingsTexts,
            featureFlags
        })
    });
};
const DaysDropdown = (param)=>{
    let { options, minutes, setMinutes } = param;
    const { EditNotificationSettingsModalTID } = useMemo(()=>GetEditNotificationSettingsModalTID(), []);
    const { t: commonTexts } = useLocaleKeys('common');
    return /*#__PURE__*/ _jsx(DropdownButton, {
        dropdownItems: options.map((daysOption)=>/*#__PURE__*/ _jsx(DropdownMenu.Item, {
                bi: {
                    name: 'edit_days'
                },
                onClick: ()=>setMinutes(daysToMinutes(daysOption)),
                testId: EditNotificationSettingsModalTID.DaysItem(daysOption),
                children: /*#__PURE__*/ _jsx(Typography, {
                    children: commonTexts.units.daysCount({
                        count: daysOption
                    })
                })
            }, daysOption)),
        testId: EditNotificationSettingsModalTID.Days(),
        children: isDays(minutes) ? commonTexts.units.daysCount({
            count: minutesToDays(minutes)
        }) : commonTexts.units.minutesCount({
            count: minutes
        })
    });
};
const TimesDropdown = (param)=>{
    let { times, setTimes } = param;
    const { EditNotificationSettingsModalTID } = useMemo(()=>GetEditNotificationSettingsModalTID(), []);
    const { t: commonTexts } = useLocaleKeys('common');
    return /*#__PURE__*/ _jsx(DropdownButton, {
        dropdownItems: getClientReminderTimes().map((timesOption)=>/*#__PURE__*/ _jsx(DropdownMenu.Item, {
                bi: {
                    name: 'edit_times'
                },
                onClick: ()=>setTimes(timesOption),
                testId: EditNotificationSettingsModalTID.TimesItem(timesOption),
                children: /*#__PURE__*/ _jsx(Typography, {
                    children: commonTexts.units.timesCountInclusive({
                        count: timesOption
                    })
                })
            }, timesOption)),
        testId: EditNotificationSettingsModalTID.Times(),
        children: commonTexts.units.timesCountInclusive({
            count: times
        })
    });
};
